import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, CalendarDays, Users, Search } from "lucide-react";
import { Kicker } from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920";

const lineAnim = (delay) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] },
});

const STATS = [
  ["12+", "Years crafting journeys"],
  ["40,000+", "Happy travellers"],
  ["6", "Curated destinations"],
  ["4.9", "Traveller rating"],
];

export default function Hero({ destinations, onSearch, onEnquire }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const [dest, setDest] = useState("All");
  const [month, setMonth] = useState("");
  const [travelers, setTravelers] = useState("2");

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden grain" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="Traveller watching a golden sunset over the ocean" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-onyx-900/80 via-onyx-900/55 to-onyx-900" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-36 pb-16">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
          <Kicker>Tripsandtrick Travel Pvt. Ltd. — Est. Journeys</Kicker>
        </motion.div>

        <h1 className="mt-6 font-serif tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-8xl text-slate-50" data-testid="hero-headline">
          <span className="block overflow-hidden pb-1"><motion.span className="block" {...lineAnim(0.35)}>Where the world</motion.span></span>
          <span className="block overflow-hidden pb-2"><motion.span className="block" {...lineAnim(0.55)}>becomes <em className="gold-text not-italic font-bold">your story.</em></motion.span></span>
        </h1>

        <motion.p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }}>
          All-inclusive international holidays — flights, handpicked hotels, private transfers
          and guided sightseeing, composed into one seamless escape.
        </motion.p>

        <motion.div className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}>
          <button onClick={() => onSearch(dest)} data-testid="hero-explore-btn"
            className="group rounded-full bg-gold px-8 py-4 text-sm font-semibold text-onyx-900 hover:bg-gold-light transition-colors flex items-center gap-2">
            Explore Packages <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button onClick={onEnquire} data-testid="hero-enquire-now-btn"
            className="rounded-full border border-white/25 px-8 py-4 text-sm font-semibold text-slate-100 hover:border-gold hover:text-gold transition-colors">
            Plan My Trip
          </button>
        </motion.div>

        <motion.div className="mt-12 rounded-2xl border border-white/10 bg-onyx-800/70 backdrop-blur-xl p-4 sm:p-5 flex flex-col lg:flex-row gap-4 lg:items-end"
          data-testid="hero-search-widget"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8 }}>
          <label className="flex-1 block">
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2"><MapPin size={12} /> Destination</span>
            <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="hero-destination-select" className="input-dark">
              <option value="All">Anywhere wonderful</option>
              {destinations.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </label>
          <label className="flex-1 block">
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2"><CalendarDays size={12} /> Travel month</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="hero-month-input" className="input-dark" />
          </label>
          <label className="flex-1 block">
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2"><Users size={12} /> Travellers</span>
            <select value={travelers} onChange={(e) => setTravelers(e.target.value)} data-testid="hero-travelers-select" className="input-dark">
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} {n === 1 ? "traveller" : "travellers"}</option>)}
            </select>
          </label>
          <button onClick={() => onSearch(dest)} data-testid="hero-search-btn"
            className="rounded-xl bg-gold px-8 py-3.5 text-sm font-semibold text-onyx-900 hover:bg-gold-light transition-colors flex items-center justify-center gap-2">
            <Search size={16} /> Find my escape
          </button>
        </motion.div>

        <motion.div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.45, duration: 0.9 }}>
          {STATS.map(([num, label]) => (
            <div key={label} className="border-l border-gold/30 pl-4">
              <div className="font-serif text-3xl text-slate-50">{num}</div>
              <div className="text-xs text-slate-400 mt-1 tracking-wide">{label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
